import React from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Typography, Grid, Chip, Box, Divider, Stack, IconButton, useTheme, alpha,
} from '@mui/material';
import { Close, TrendingUp } from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import {
  Revenue, REVENUE_COUNTERPARTY_KIND_LABELS,
} from '../../types/revenues';
import { formatMoneyFull } from '../reports/finance/chartConfig';
import { toUploadUrl } from '../../utils/uploadUrl';
import { PAYMENT_METHOD_LABEL } from '../../utils/paymentMethods';

interface Props {
  open: boolean;
  revenue: Revenue | null;
  onClose: () => void;
}

const ViewRevenueDialog: React.FC<Props> = ({ open, revenue, onClose }) => {
  const theme = useTheme();
  const { currency } = useAuth();
  if (!revenue) return null;

  const docUrl = revenue.documentFilename ? toUploadUrl('revenue-documents', revenue.documentFilename) : null;
  const isReversed = !revenue.isActive;

  const Row = ({ label, children }: { label: string; children: React.ReactNode }) => (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }}>
        {label}
      </Typography>
      <Box sx={{ mt: 0.5 }}>{children}</Box>
    </Box>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
      <DialogTitle component="div" sx={{ pb: 1.5 }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box
            sx={{
              width: 36, height: 36, borderRadius: 1.5,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              bgcolor: alpha(theme.palette.success.main, 0.12),
              color: theme.palette.success.main,
              flexShrink: 0,
            }}
          >
            <TrendingUp />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h6" fontWeight={700}>Revenue Details</Typography>
            <Chip
              label={isReversed ? 'Reversed' : 'Posted to ledger'}
              color={isReversed ? 'warning' : 'success'}
              size="small"
              sx={{ mt: 0.25 }}
            />
          </Box>
          <IconButton size="small" onClick={onClose}><Close /></IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        <Grid container spacing={2}>
          <Grid size={12}>
            <Row label="Title">
              <Typography variant="body1" fontWeight={500}>{revenue.title}</Typography>
            </Row>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <Row label="Amount">
              <Typography variant="h5" fontWeight={700} sx={{ color: theme.palette.success.main }}>
                {formatMoneyFull(revenue.amount, currency)}
              </Typography>
            </Row>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Row label="Business date">
              <Typography variant="body1">{new Date(revenue.date).toLocaleDateString()}</Typography>
            </Row>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <Row label="Method">
              <Chip label={PAYMENT_METHOD_LABEL[revenue.method]} size="small" variant="outlined" />
            </Row>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Row label="Reference">
              <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{revenue.referenceNumber || '—'}</Typography>
            </Row>
          </Grid>

          {revenue.categoryId && (
            <Grid size={{ xs: 12, md: 6 }}>
              <Row label="Category">
                <Typography variant="body1">{revenue.categoryId.name}</Typography>
                {revenue.subCategoryId && (
                  <Typography variant="caption" color="text.secondary">{revenue.subCategoryId.name}</Typography>
                )}
              </Row>
            </Grid>
          )}

          {revenue.counterparty && (
            <Grid size={{ xs: 12, md: 6 }}>
              <Row label="Received from">
                <Typography variant="body1">{revenue.counterparty.name}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {REVENUE_COUNTERPARTY_KIND_LABELS[revenue.counterparty.kind]}
                </Typography>
              </Row>
            </Grid>
          )}

          {revenue.notes && (
            <Grid size={12}>
              <Row label="Notes">
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{revenue.notes}</Typography>
              </Row>
            </Grid>
          )}

          <Grid size={12}><Divider /></Grid>

          <Grid size={12}>
            <Row label="Recorded by">
              <Typography variant="body1">{revenue.createdBy?.name || '—'}</Typography>
              <Typography variant="caption" color="text.secondary">
                {new Date(revenue.createdAt).toLocaleString()}
              </Typography>
            </Row>
          </Grid>

          {docUrl && (
            <>
              <Grid size={12}><Divider /></Grid>
              <Grid size={12}>
                <Row label="Attachment">
                  {revenue.documentType === 'image' ? (
                    <Box
                      component="img"
                      src={docUrl}
                      alt={revenue.documentName}
                      sx={{ maxWidth: '100%', maxHeight: 300, borderRadius: 1, border: '1px solid', borderColor: 'divider', mt: 0.5 }}
                    />
                  ) : (
                    <Button variant="outlined" href={docUrl} target="_blank" rel="noopener noreferrer" sx={{ textTransform: 'none' }}>
                      View PDF: {revenue.documentName}
                    </Button>
                  )}
                </Row>
              </Grid>
            </>
          )}
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ViewRevenueDialog;
